import { AddressActions } from "../components/address-actions";
import { JobEmails } from "../components/email-dialog";
import { formatPhone, type JobDetail } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { del, errorMessage, get, patch, post, put } from "../api";
import { AddressFields, ContactPicker, MoneyField } from "../components/forms";
import { History, Notes, RelatedContacts } from "../components/records";
import { FilesPanel } from "../components/files";
import { JobProposals } from "./Proposal";
import { JobWorkOrders } from "./WorkOrders";
import { CommsPanel } from "./Comms";
import { JobReceipts } from "./Receipts";
import { JobInvoices } from "./Invoices";
import { JobCompletion } from "./Completion";
import { JobFeedback } from "./Survey";
import { JobChangeOrders } from "./ChangeOrders";
import { JobPermitsWarranty, PermitReminder } from "./PermitsWarranty";
import { JobMaterials } from "./Materials";
import { JobPnl } from "./JobPnl";
import { LinkQbJobDialog } from "../components/qb-link-job";
import { AppointmentsPanel, PeopleChips, TasksPanel } from "../components/schedule";
import { Alert, Badge, Button, Dialog, Field, Loading, Panel, PanelHeader, SelectField, StageBadge, Tabs, TextAreaField, addressLine, ago, formatCents, formatDate } from "../components/ui";
import { useUsers, useWorkflows } from "../lookups";
import { useSession } from "../session";
import { useRememberedTab } from "../components/use-tab";
import { RichText } from "../components/rich-text";

type Tab = "overview" | "pnl" | "files" | "permits" | "invoices" | "expenses" | "comms" | "proposals" | "workorders" | "materials" | "details" | "history";

export function JobDetailPage() {
  const { id = "" } = useParams();
  const { can, me } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [linkingQb, setLinkingQb] = useState(false);
  // ?tab=files opens a tab directly (the camera's Done goes to the job's photos).
  const [tab, setTab] = useRememberedTab<Tab>("job", ["overview", "pnl", "files", "permits", "invoices", "expenses", "comms", "proposals", "workorders", "materials", "details", "history"], "overview");
  const job = useQuery({ queryKey: ["job", id], queryFn: () => get<JobDetail>(`/api/jobs/${id}`) });

  const onSaved = (j: JobDetail) => {
    qc.setQueryData(["job", id], j);
    void qc.invalidateQueries({ queryKey: ["job-history", id] });
    void qc.invalidateQueries({ queryKey: ["jobs"] });
    void qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const move = useMutation({ mutationFn: (stageId: string) => post<JobDetail>(`/api/jobs/${id}/stage`, { stageId }), onSuccess: onSaved });
  const remove = useMutation({
    mutationFn: () => del(`/api/jobs/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["jobs"] });
      navigate(`/contacts/${job.data?.customerId}`);
    },
  });
  const restore = useMutation({ mutationFn: () => post<JobDetail>(`/api/jobs/${id}/restore`), onSuccess: onSaved });

  if (job.isLoading) return <Loading />;
  if (job.error) return <Alert>{errorMessage(job.error)}</Alert>;
  const j = job.data!;
  const address = addressLine(j);
  const phone = j.customer.phone || j.customer.phone2;
  const stageIndex = j.stages.findIndex((s) => s.id === j.stageId);
  // One-click move to the next stage (never to "Lost"; that's picked from the list).
  const following = j.stages[stageIndex + 1];
  const nextStage = following && following.kind !== "lost" ? following : undefined;

  return (
    <div className="flex flex-col gap-5">
      {j.deletedAt && (
        <Alert>
          This job is in the recycle bin.{" "}
          {can("records.delete") && (
            <button className="font-semibold underline" onClick={() => restore.mutate()}>
              Restore it
            </button>
          )}
        </Alert>
      )}

      <Panel className="overflow-hidden">
        <div className="flex flex-wrap items-start justify-between gap-4 px-5 pt-5">
          <div className="flex min-w-0 gap-4">
          <StreetView job={j} />
          <div className="min-w-0">
            <div className="text-sm text-muted">
              <Link to="/jobs" className="hover:text-ink">
                Jobs
              </Link>{" "}
              · {j.workflowName} · Job #{j.number}
            </div>
            <h1 className="page-title mt-1">
              <Link to={`/contacts/${j.customerId}`} className="hover:text-brand">
                {j.customerName}
              </Link>
              {j.title && <span className="font-normal text-ink-soft"> — {j.title}</span>}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-soft">
              {address && <span>{address}</span>}
              {phone && <span>{formatPhone(phone)}</span>}
              {j.salesRep && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="size-2.5 rounded-full" style={{ background: j.salesRep.color }} aria-hidden />
                  {j.salesRep.name}
                </span>
              )}
              {j.otherReps.map((r) => (
                <span key={r.id} className="inline-flex items-center gap-1.5">
                  <span className="size-2.5 rounded-full" style={{ background: r.color }} aria-hidden />
                  {r.name}
                </span>
              ))}
            </div>
          </div>
          </div>
          {/* On a phone the stage gets its own full-width rows: the dropdown, then the next-stage button. */}
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:items-end">
            {can("jobs.edit") && !j.deletedAt ? (
              <div className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-2 sm:flex sm:w-auto">
                <label htmlFor="stage" className="text-sm font-semibold text-ink-soft">
                  Stage
                </label>
                <select
                  id="stage"
                  value={j.stageId}
                  onChange={(e) => move.mutate(e.target.value)}
                  disabled={move.isPending}
                  className="w-full min-w-0 rounded-[var(--radius-control)] border border-line bg-white px-3 py-1.5 font-semibold focus:border-brand focus:outline-none sm:w-auto"
                >
                  {j.stages.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {nextStage && (
                  <Button variant="primary" className="col-span-2 px-3 py-1.5 sm:col-auto" onClick={() => move.mutate(nextStage.id)} busy={move.isPending}>
                    → {nextStage.name}
                  </Button>
                )}
              </div>
            ) : (
              <StageBadge name={j.stageName} kind={j.stageKind} />
            )}
            <span className="text-xs text-muted">In this stage since {ago(j.stageChangedAt)}</span>
          </div>
        </div>
        {move.error && (
          <div className="px-5 pt-3">
            <Alert>{errorMessage(move.error)}</Alert>
          </div>
        )}

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2 px-5 py-4">
          {phone && <QuickAction href={`tel:${phone}`}>Call</QuickAction>}
          {j.customer.phone && <QuickAction href={`sms:${j.customer.phone}`}>Text</QuickAction>}
          {j.customer.email && <QuickAction href={`mailto:${j.customer.email}`}>Email</QuickAction>}
          {address && <QuickAction href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`}>Directions</QuickAction>}
          {address && <AddressActions address={address} jobId={j.id} />}
        </div>

        {j.money && (
          <div className={`grid border-t border-mist ${j.money.creditCents > 0 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
            <Money label="Contract" cents={j.money.contractCents} />
            <Money label="Paid" cents={j.money.paidCents} />
            {j.money.creditCents > 0 && <Money label="Credits" cents={j.money.creditCents} />}
            <Money label="Balance" cents={j.money.balanceCents} strong />
          </div>
        )}
      </Panel>

      <Tabs<Tab>
        tabs={[
          { value: "overview", label: "Overview" },
          { value: "details", label: "Details" },
          ...(can("comms.view") ? [{ value: "comms" as const, label: "Communication" }] : []),
          { value: "files", label: "Files and photos" },
          ...(can("money.view") ? [{ value: "proposals" as const, label: "Proposals" }] : []),
          { value: "materials", label: "Materials" },
          { value: "workorders", label: "Work orders" },
          { value: "permits", label: "Permits & warranty" },
          ...(can("money.view") ? [{ value: "invoices" as const, label: "Invoices & payments" }] : []),
          ...(can("receipts.submit", "receipts.approve") ? [{ value: "expenses" as const, label: "Expenses" }] : []),
          ...(can("reports.qbPnl") ? [{ value: "pnl" as const, label: "P&L" }] : []),
          { value: "history", label: "History" },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === "overview" && (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <Notes path={`/api/jobs/${id}/notes`} queryKey={["job-notes", id]} />
          <div className="flex flex-col gap-5">
            <PermitReminder jobId={id} city={j.city} />
            <PropertyRecords job={j} />
            {j.insurance && <InsuranceSummary job={j} onEdit={() => setTab("details")} />}
            <AppointmentsPanel jobId={id} label={`#${j.number} ${j.customerName}`} />
            <TasksPanel jobId={id} />
            <JobFeedback jobId={id} />
            <RelatedContacts
              contacts={j.contacts}
              basePath={`/api/jobs/${id}`}
              invalidate={["job", id]}
              customer={{ contactId: j.customer.id, role: "Customer", displayName: j.customer.displayName, typeName: "", company: "", email: j.customer.email, phone: j.customer.phone }}
            />
            <Panel>
              <PanelHeader title="About this job" />
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 px-5 py-4 text-sm">
                <dt className="text-muted">Lead source</dt>
                <dd>{j.leadSource || "—"}</dd>
                {j.referredBy && (
                  <>
                    <dt className="text-muted">Referred by</dt>
                    <dd>
                      <Link to={`/contacts/${j.referredBy.id}`} className="hover:text-brand">{j.referredBy.name}</Link>
                    </dd>
                  </>
                )}
                <dt className="text-muted">Added</dt>
                <dd>{formatDate(j.createdAt, me?.company.timezone)}</dd>
                <dt className="text-muted">QuickBooks</dt>
                <dd>
                  {j.qbSubCustomerId && !j.qbSubCustomerId.startsWith("pending:") ? (
                    <>
                      <Badge tone="ok">Linked</Badge> {j.qbName}
                      {can("jobs.edit") && <QbRename jobId={id} name={j.qbName} linked />}
                      <div className="text-xs text-muted">QuickBooks sub-customer #{j.qbSubCustomerId}</div>
                    </>
                  ) : (
                    <>
                      <Badge tone="wait">Not in QuickBooks yet</Badge>
                      <div className="text-muted">
                        Will be named “{j.qbName}”{can("jobs.edit") && <QbRename jobId={id} name={j.qbName} linked={false} />}. {j.qbCreateOnNew ? "It's sent when the job is made; if it isn't in QuickBooks yet, it's waiting (see Settings → QuickBooks → Outbox)." : j.qbCreateStageName ? `Created when the job reaches ${j.qbCreateStageName}.` : "Not created automatically for this job type."} To link one already in QuickBooks, use Settings → QuickBooks → Find matches.
                      </div>
                      {can("jobs.edit") && <QbNow jobId={id} />}
                      {can("jobs.edit") && (
                        <button className="mt-1 block text-sm font-semibold text-brand hover:underline" onClick={() => setLinkingQb(true)}>
                          It's already in QuickBooks: link it…
                        </button>
                      )}
                      {linkingQb && <LinkQbJobDialog jobId={id} jobLabel={`job #${j.number}`} initial={j.customer.lastName || j.customerName} onClose={() => setLinkingQb(false)} />}
                    </>
                  )}
                </dd>
                {j.description && (
                  <>
                    <dt className="text-muted">Description</dt>
                    <dd><RichText text={j.description} /></dd>
                  </>
                )}
              </dl>
            </Panel>
          </div>
        </div>
      )}

      {tab === "files" && <FilesPanel owner={{ kind: "job", id }} readOnly={!!j.deletedAt} />}
      {tab === "proposals" && (
        <div className="flex flex-col gap-5">
          <JobProposals jobId={id} />
          <JobChangeOrders jobId={id} />
        </div>
      )}
      {tab === "workorders" && <JobWorkOrders jobId={id} />}
      {tab === "expenses" && <JobReceipts jobId={id} />}
      {tab === "invoices" && (
        <div className="flex flex-col gap-5">
          <JobInvoices jobId={id} />
          {j.insurance && <JobCompletion jobId={id} />}
        </div>
      )}
      {tab === "permits" && <JobPermitsWarranty jobId={id} />}
      {tab === "materials" && <JobMaterials jobId={id} />}
      {tab === "comms" && (
        <div className="flex flex-col gap-5">
          <JobEmails jobId={id} />
          <CommsPanel phone={j.customer.phone} contact={{ id: j.customer.id, name: j.customer.displayName }} jobId={id} />
        </div>
      )}
      {tab === "details" && <JobForm job={j} onSaved={onSaved} />}
      {tab === "pnl" && <JobPnl jobId={id} />}
      {tab === "history" && <History path={`/api/jobs/${id}/history`} queryKey={["job-history", id]} />}

      {can("records.delete") && !j.deletedAt && tab === "details" && (
        <div className="flex justify-end">
          <Button
            variant="danger"
            busy={remove.isPending}
            onClick={() => {
              if (window.confirm(`Move job #${j.number} to the recycle bin? You can restore it from Settings → Recycle bin.`)) remove.mutate();
            }}
          >
            Delete job
          </Button>
        </div>
      )}
    </div>
  );
}

function QuickAction({ href, children }: { href: string; children: ReactNode }) {
  const external = href.startsWith("http");
  return (
    <a
      href={href}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      className="rounded-full border border-line bg-white px-3.5 py-1.5 text-sm font-semibold text-ink-soft hover:border-brand hover:text-brand"
    >
      {children}
    </a>
  );
}

function Money({ label, cents, strong }: { label: string; cents: number; strong?: boolean }) {
  return (
    <div className="border-r border-mist px-5 py-3 last:border-0">
      <div className="text-xs font-medium tracking-wide text-muted uppercase">{label}</div>
      <div className={strong ? "text-lg font-bold" : "text-lg font-semibold"}>{formatCents(cents)}</div>
    </div>
  );
}

function InsuranceSummary({ job, onEdit }: { job: JobDetail; onEdit: () => void }) {
  const adjuster = job.contacts.find((c) => /adjuster/i.test(c.role));
  return (
    <Panel>
      <PanelHeader
        title="Insurance"
        action={
          <button className="text-sm font-semibold text-brand hover:underline" onClick={onEdit}>
            Edit
          </button>
        }
      />
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 px-5 py-4 text-sm">
        <dt className="text-muted">Carrier</dt>
        <dd>{job.insuranceCarrier || "—"}</dd>
        <dt className="text-muted">Claim #</dt>
        <dd>{job.claimNumber || "—"}</dd>
        <dt className="text-muted">Policy #</dt>
        <dd>{job.policyNumber || "—"}</dd>
        <dt className="text-muted">Date of loss</dt>
        <dd>{formatDate(job.dateOfLoss)}</dd>
        <dt className="text-muted">Deductible</dt>
        <dd>{formatCents(job.deductibleCents)}</dd>
        <dt className="text-muted">Adjuster</dt>
        <dd>{adjuster ? `${adjuster.displayName}${adjuster.phone ? ` · ${formatPhone(adjuster.phone)}` : ""}` : "Add one under Related contacts"}</dd>
      </dl>
    </Panel>
  );
}

function JobForm({ job, onSaved }: { job: JobDetail; onSaved: (j: JobDetail) => void }) {
  const { can, me } = useSession();
  const workflows = useWorkflows();
  const users = useUsers();
  const initial = () => ({
    title: job.title,
    street: job.street,
    street2: job.street2,
    city: job.city,
    state: job.state,
    zip: job.zip,
    leadSource: job.leadSource,
    referredBy: job.referredBy,
    salesRepId: job.salesRep?.id ?? "",
    otherRepIds: job.otherReps.map((r) => r.id),
    description: job.description,
    insuranceCarrier: job.insuranceCarrier,
    claimNumber: job.claimNumber,
    policyNumber: job.policyNumber,
    dateOfLoss: job.dateOfLoss ?? "",
    deductibleCents: job.deductibleCents,
  });
  const [form, setForm] = useState(initial);
  const [changingType, setChangingType] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => setForm(initial()), [job]);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: () => patch<JobDetail>(`/api/jobs/${job.id}`, { ...(({ referredBy: _r, ...rest }) => rest)(form), referredById: form.referredBy?.id ?? null, salesRepId: form.salesRepId || null, otherRepIds: form.otherRepIds.filter((x) => x !== form.salesRepId), dateOfLoss: form.dateOfLoss || null }),
    onSuccess: (j) => {
      onSaved(j);
      setSaved(true);
    },
  });
  const editable = can("jobs.edit") && !job.deletedAt;
  const leadSources = me?.lists.leadSources ?? [];

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        setSaved(false);
        save.mutate();
      }}
    >
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      {saved && <Alert tone="ok">Saved.</Alert>}
      <fieldset disabled={!editable} className="flex flex-col gap-5">
        <Panel>
          <PanelHeader
            title="Job"
            action={
              editable && (
                <Button type="button" className="px-3 py-1.5" onClick={() => setChangingType(true)}>
                  Change job type
                </Button>
              )
            }
          />
          <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
            <Field className="sm:col-span-2" label="Short description" value={form.title} onChange={set("title")} placeholder="Roof and gutters" />
            <SelectField label="Commissioned rep" value={form.salesRepId} onChange={set("salesRepId")} hint="Gets the commission. Add anyone else working it below.">
              <option value="">Nobody yet</option>
              {users.data
                ?.filter((u) => u.active || u.id === form.salesRepId)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </SelectField>
            <SelectField label="Lead source" value={form.leadSource} onChange={set("leadSource")}>
              <option value="">Not set</option>
              {[...leadSources, ...(form.leadSource && !leadSources.includes(form.leadSource) ? [form.leadSource] : [])].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </SelectField>
            <div className="sm:col-span-2">
              <PeopleChips legend="Other reps (optional)" value={form.otherRepIds} onChange={(ids) => setForm((f) => ({ ...f, otherRepIds: ids }))} exclude={form.salesRepId || null} hint="They see the job in their jobs and on the map, and get its tasks. No commission." />
            </div>
            <ContactPicker label="Referred by" value={form.referredBy} onChange={(c) => setForm((f) => ({ ...f, referredBy: c }))} hint="Who sent us this job, if anyone." />
            <TextAreaField className="sm:col-span-2" label="Description" value={form.description} onChange={set("description")} rows={4} />
          </div>
        </Panel>

        <Panel>
          <PanelHeader title="Job address" description="Where the work is. Can differ from the customer's mailing address." />
          <div className="px-5 py-5">
            <AddressFields value={form} onChange={(a) => setForm((f) => ({ ...f, ...a }))} />
          </div>
        </Panel>

        {job.insurance && (
          <Panel>
            <PanelHeader title="Insurance" />
            <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
              <Field label="Carrier" value={form.insuranceCarrier} onChange={set("insuranceCarrier")} />
              <Field label="Claim #" value={form.claimNumber} onChange={set("claimNumber")} />
              <Field label="Policy #" value={form.policyNumber} onChange={set("policyNumber")} />
              <Field label="Date of loss" type="date" value={form.dateOfLoss} onChange={set("dateOfLoss")} />
              <MoneyField label="Deductible" cents={form.deductibleCents} onChange={(c) => setForm((f) => ({ ...f, deductibleCents: c }))} />
            </div>
          </Panel>
        )}
      </fieldset>
      {editable && (
        <div className="flex justify-end">
          <Button variant="primary" type="submit" busy={save.isPending}>
            Save changes
          </Button>
        </div>
      )}
      {changingType && workflows.data && <ChangeTypeDialog job={job} onClose={() => setChangingType(false)} onSaved={onSaved} />}
    </form>
  );
}

function ChangeTypeDialog({ job, onClose, onSaved }: { job: JobDetail; onClose: () => void; onSaved: (j: JobDetail) => void }) {
  const workflows = useWorkflows();
  const others = workflows.data?.filter((w) => w.id !== job.workflowId) ?? [];
  const [workflowId, setWorkflowId] = useState(others[0]?.id ?? "");
  const stages = others.find((w) => w.id === workflowId)?.stages ?? [];
  const [stageId, setStageId] = useState("");
  useEffect(() => {
    // Keep the same stage name if the new workflow has one.
    setStageId(stages.find((s) => s.name === job.stageName)?.id ?? stages[0]?.id ?? "");
  }, [workflowId]);
  const save = useMutation({
    mutationFn: () => patch<JobDetail>(`/api/jobs/${job.id}`, { workflowId, stageId }),
    onSuccess: (j) => {
      onSaved(j);
      onClose();
    },
  });
  return (
    <Dialog
      title="Change job type"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="button" busy={save.isPending} disabled={!workflowId || !stageId} onClick={() => save.mutate()}>
            Change
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <p className="text-sm text-ink-soft">Now {job.workflowName}, at {job.stageName}.</p>
        <SelectField label="New job type" value={workflowId} onChange={(e) => setWorkflowId(e.target.value)}>
          {others.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </SelectField>
        <SelectField label="Stage in the new type" value={stageId} onChange={(e) => setStageId(e.target.value)}>
          {stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
      </div>
    </Dialog>
  );
}

/** The job's QuickBooks name: changing it renames the job in QuickBooks when it's there. */
function QbRename({ jobId, name, linked }: { jobId: string; name: string; linked: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const save = useMutation({
    mutationFn: () => put<{ inQuickBooks: boolean; dryRun: boolean }>(`/api/jobs/${jobId}/qb-name`, { name: value }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["job", jobId] });
      setEditing(false);
    },
  });
  if (!editing)
    return (
      <button className="ml-2 text-xs font-semibold text-brand hover:underline" onClick={() => (setValue(name), setEditing(true))}>
        {linked ? "Rename" : "Change"}
      </button>
    );
  return (
    <Dialog
      title="QuickBooks name"
      onClose={() => setEditing(false)}
      footer={
        <>
          <Button variant="quiet" onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!value.trim() || value.trim() === name} busy={save.isPending} onClick={() => save.mutate()}>
            {linked ? "Rename in QuickBooks" : "Save"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Name" value={value} onChange={(e) => setValue(e.target.value)} maxLength={100} autoFocus hint={linked ? "Renames the job in QuickBooks too (recorded as a dry run while QuickBooks writes are off). Include the street so it's easy to find there." : "The name it gets when it's created in QuickBooks."} />
      </div>
    </Dialog>
  );
}

/** "Create in QuickBooks now": the job's customer and sub-customer (a dry run until writes are on). */
function QbNow({ jobId }: { jobId: string }) {
  const qc = useQueryClient();
  const go = useMutation({ mutationFn: () => post<{ qbId: string; dryRun: boolean }>(`/api/jobs/${jobId}/quickbooks`), onSuccess: () => qc.invalidateQueries({ queryKey: ["job", jobId] }) });
  return (
    <div className="mt-1">
      <button className="text-sm font-semibold text-brand hover:underline disabled:text-muted" disabled={go.isPending} onClick={() => go.mutate()}>
        Create in QuickBooks now
      </button>
      {go.data && <span className="ml-2 text-xs text-muted">{go.data.dryRun ? "Recorded as a dry run (writes are off)." : "Created."}</span>}
      {go.error && <span className="ml-2 text-xs text-danger">{errorMessage(go.error)}</span>}
    </div>
  );
}

/** The house at a glance: a photo tagged "Street View", else Google Street View when it has the address. */
function StreetView({ job }: { job: JobDetail }) {
  const [failed, setFailed] = useState(false);
  const src = job.streetView.photoUrl ?? (failed ? null : job.streetView.googleUrl);
  if (!src) return null;
  const href = job.streetView.photoUrl ? `/jobs/${job.id}?tab=files` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine(job))}`;
  const inner = <img src={src} alt={job.streetView.photoUrl ? "Street View photo" : "Google Street View"} className="h-20 w-28 shrink-0 rounded-[var(--radius-control)] bg-mist object-cover sm:h-24 sm:w-36" loading="lazy" onError={() => setFailed(true)} />;
  return job.streetView.photoUrl ? (
    <Link to={href} title="Street View photo (tag a photo Street View to change it)" className="shrink-0">
      {inner}
    </Link>
  ) : (
    <a href={href} target="_blank" rel="noreferrer" title="Google Street View. Tag one of the job's photos Street View to show it here instead." className="shrink-0">
      {inner}
    </a>
  );
}

const usd = (c: number | null) => (c === null ? null : formatCents(c).replace(/\.00$/, ""));

/** What the county has on the property (looked up once when the job was made). */
function PropertyRecords({ job }: { job: JobDetail }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const look = useMutation({ mutationFn: () => post<JobDetail>(`/api/jobs/${job.id}/property`), onSuccess: (d) => qc.setQueryData(["job", job.id], d) });
  const p = job.property;
  const r = p?.record ?? null;
  if (!r) {
    if (!can("jobs.edit") && !p?.searchUrl) return null;
    return (
      <Panel>
        <PanelHeader title="Property records" />
        <div className="flex flex-col gap-2 px-5 py-4 text-sm">
          {p ? <p className="text-ink-soft">{p.note}</p> : <p className="text-ink-soft">Not looked up yet (this job was made before county records were added).</p>}
          <div className="flex flex-wrap gap-2">
            {p?.searchUrl && (
              <a href={p.searchUrl} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline">
                Look up on the assessor's site
              </a>
            )}
            {can("jobs.edit") && (!p || p.status === "failed" || p.status === "not_found") && (
              <Button variant="quiet" className="px-2 py-1" busy={look.isPending} onClick={() => look.mutate()}>
                Look up county records
              </Button>
            )}
          </div>
          {look.error && <Alert>{errorMessage(look.error)}</Alert>}
        </div>
      </Panel>
    );
  }
  const rows: [string, string | null][] = [
    ["Owner", [r.owner, r.owner2].filter(Boolean).join("; ") || null],
    ["Owner's mailing address", r.mailingAddress || null],
    ["County's address", r.situs || null],
    ["Market value", usd(r.marketValueCents)],
    ["Assessed value", usd(r.assessedValueCents)],
    ["Land / building value", r.landValueCents !== null || r.improvementValueCents !== null ? [usd(r.landValueCents) ?? "—", usd(r.improvementValueCents) ?? "—"].join(" / ") : null],
    ["Building", [r.building, r.sqft ? `${r.sqft.toLocaleString()} sq ft` : null, r.yearBuilt ? `built ${r.yearBuilt}` : null, r.stories].filter(Boolean).join(" · ") || null],
    ["Lot", r.acres ? `${r.acres} acres` : null],
    ["Subdivision", r.subdivision || null],
    ["Last sale", r.lastSale ? [r.lastSale, usd(r.lastSalePriceCents)].filter(Boolean).join(" · ") : null],
    ["Account", [r.accountNo, r.parcelId].filter(Boolean).join(" · ") || null],
  ];
  return (
    <Panel>
      <PanelHeader title="Property records" description={`${r.source}${r.asOf ? `, county data as of ${r.asOf}` : ""}. Looked up ${p ? formatDate(p.checkedAt) : ""}; check the assessor for anything recent.`} />
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 px-5 py-4 text-sm">
        {rows
          .filter((x): x is [string, string] => !!x[1])
          .map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted">{k}</dt>
              <dd className="break-words">{v}</dd>
            </div>
          ))}
      </dl>
      {(r.recordUrl || r.searchUrl) && (
        <div className="border-t border-mist px-5 py-3 text-sm">
          <a href={r.recordUrl ?? r.searchUrl!} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline">
            {r.recordUrl ? "Open the assessor's record" : `Open the assessor's search (account ${r.accountNo})`}
          </a>
        </div>
      )}
    </Panel>
  );
}
